import { useState, useEffect } from 'react';
import { LayoutGrid, Calendar, Scissors, Users, UserCircle, Megaphone, BookOpen, type LucideIcon } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useSubscription } from '../../hooks/useSubscription';

export interface MenuItem {
    icon: LucideIcon;
    label: string;
    path: string;
    mobileLabel?: string;
}

export const menuItems: MenuItem[] = [
    { icon: LayoutGrid, label: 'Dashboard', path: '/app', mobileLabel: 'Inicio' },
    { icon: Calendar, label: 'Agenda', path: '/app/agenda' },
    { icon: Scissors, label: 'Servicios', path: '/app/services' },
    { icon: Users, label: 'Empleados', path: '/app/employees' },
    { icon: UserCircle, label: 'Clientes', path: '/app/clients' },
    { icon: Megaphone, label: 'Campañas', path: '/app/campaigns' },
    { icon: BookOpen, label: 'Facturación (Demo)', path: '/app/pdf-demo' },
];

export const mobileNavItems: MenuItem[] = [
    menuItems[0],
    menuItems[1],
    menuItems[3],
    menuItems[4],
];

const Sidebar = () => {
    const location = useLocation();
    const [logoUrl, setLogoUrl] = useState<string | null>(null);
    const { isOwner, isManager, isLoading, isPaid, daysRemaining } = useSubscription();

    useEffect(() => {
        const storedLogo = localStorage.getItem('salonLogoUrl');
        if (storedLogo) setLogoUrl(storedLogo);

        const handleStorageChange = () => {
            const updatedLogo = localStorage.getItem('salonLogoUrl');
            setLogoUrl(updatedLogo);
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    const filteredItems = menuItems.filter(item => {
        if (item.path === '/app/employees') return isOwner || isManager;
        if (item.path === '/app/campaigns') return isOwner || isManager;
        return true;
    });

    return (
        <aside className="fixed left-0 top-0 h-screen w-64 bg-white border-r border-gray-100 hidden md:flex flex-col z-50 shadow-[4px_0_24px_rgba(0,0,0,0.02)]">
            <div className="p-6 flex items-center justify-center h-24">
                {logoUrl ? (
                    <img
                        src={logoUrl}
                        alt="Logo"
                        className="max-h-14 max-w-[180px] object-contain transition-all hover:scale-105"
                        onError={() => setLogoUrl(null)}
                    />
                ) : (
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-primary-light)]"></div>
                        <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">Cresta</h1>
                    </div>
                )}
            </div>

            <nav className="flex-1 px-4 space-y-2 mt-2 overflow-y-auto py-4">
                <p className="px-4 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Menu Principal</p>
                {filteredItems.map((item) => {
                    const isActive = location.pathname === item.path ||
                        (item.path !== '/app' && location.pathname.startsWith(item.path));
                    return (
                        <Link
                            key={item.path}
                            to={item.path}
                            className={`flex items-center gap-3 px-4 py-3.5 rounded-2xl transition-all duration-200 cursor-pointer group relative overflow-hidden
                                ${isActive
                                    ? 'bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-secondary)]/80 text-white font-medium shadow-md shadow-[var(--color-primary)]/20'
                                    : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'
                                }`}
                        >
                            <item.icon className={`w-5 h-5 transition-transform duration-200 ${isActive ? 'scale-110' : 'group-hover:scale-110'}`} />
                            <span>{item.label}</span>
                        </Link>
                    );
                })}
            </nav>

            <div className="p-4 border-t border-gray-50 bg-gray-50/50">
                {!isLoading && !isPaid && daysRemaining !== null && (
                    <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-4 text-white relative overflow-hidden shadow-lg shadow-gray-200">
                        {/* Decorative element */}
                        <div className="absolute top-0 right-0 w-16 h-16 bg-[var(--color-primary)]/20 rounded-full -mr-8 -mt-8 blur-2xl"></div>
                        
                        <div className="relative z-10">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-[10px] font-bold uppercase tracking-wider text-[var(--color-primary-light)]">Periodo de Prueba</span>
                                <div className="px-2 py-0.5 rounded-full bg-white/10 text-[10px] font-medium">BETA</div>
                            </div>
                            
                            <p className="text-sm font-medium text-gray-300 mb-1">Días restantes:</p>
                            <div className="flex items-baseline gap-2 mb-4">
                                <span className="text-3xl font-bold text-white">{daysRemaining}</span>
                                <span className="text-xs text-gray-400">de 14</span>
                            </div>

                            {/* Simple Progress Bar */}
                            <div className="w-full h-1.5 bg-white/10 rounded-full mb-4 overflow-hidden">
                                <div 
                                    className="h-full bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-secondary)] transition-all duration-1000"
                                    style={{ width: `${(daysRemaining / 14) * 100}%` }}
                                ></div>
                            </div>

                            <Link 
                                to="/app/settings" 
                                className="w-full py-2 bg-[var(--color-primary)] hover:bg-[var(--color-primary-dark)] text-white text-xs font-bold rounded-xl transition-all flex items-center justify-center gap-2 shadow-sm"
                            >
                                <Scissors size={14} />
                                Activar Plan Pro
                            </Link>
                        </div>
                    </div>
                )}

                {isPaid && (
                    <div className="bg-gradient-to-br from-[var(--color-primary)]/10 to-[var(--color-secondary)]/5 rounded-2xl p-4 border border-[var(--color-primary)]/10">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="w-8 h-8 rounded-full bg-[var(--color-primary)]/20 flex items-center justify-center text-[var(--color-primary)]">
                                <LayoutGrid size={16} />
                            </div>
                            <div>
                                <p className="text-xs font-bold text-gray-900">Cresta Pro</p>
                                <p className="text-[10px] text-gray-500">Suscripción Activa</p>
                            </div>
                        </div>
                        <p className="text-[10px] text-gray-400 leading-tight">
                            Tienes acceso ilimitado a todas las herramientas de gestión.
                        </p>
                    </div>
                )}
            </div>
        </aside>
    );
};

export default Sidebar;
